import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { visitorHeaders } from './visitor';

// The session fees come from the server (an admin sets them from the dashboard). The site never keeps its own copy of a fee:
// every price, GST amount and total it shows is what `GET /pricing` returned, i.e. exactly what checkout will charge.
const CACHE_MS = 60 * 1000;
let cached = null; // { data, at, who }
let inflight = null;

// The amounts depend on who is asking (their early-bird window), so what was fetched for a logged-out visitor is not reused after a login.
const whoIsAsking = () => {
  try { return window.localStorage.getItem('loginToken') ? 'member' : 'visitor'; } catch (error) { return 'visitor'; }
};

// `startOffer`: this is the offer screen, so the visitor's 24-hour early-bird window starts now (if it has not): for the logged-in account,
// or, before they have given a phone number, for their browser (logging in later adopts it).
// Every other page just reads the prices; looking at a price never starts anyone's clock.
export const fetchPricing = async ({ force = false, startOffer = false } = {}) => {
  const who = whoIsAsking();
  if (!force && cached && cached.who === who && Date.now() - cached.at < CACHE_MS) return cached.data;
  if (!force && inflight) return inflight;
  let token = null;
  try { token = window.localStorage.getItem('loginToken'); } catch (error) { /* no storage */ }
  inflight = axios
    .get(`${process.env.REACT_APP_SERVER_URL}/pricing`, {
      params: startOffer ? { startOffer: 1 } : undefined,
      headers: { ...visitorHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    .then(({ data }) => {
      const pricing = data.pricing;
      // How far this device's clock is from the server's, so the countdown shows the server's time, not the phone's.
      if (pricing.offer) pricing.offer.clockOffsetMs = Date.parse(pricing.offer.serverNow) - Date.now();
      cached = { data: pricing, at: Date.now(), who };
      return cached.data;
    })
    .finally(() => { inflight = null; });
  return inflight;
};

// { pricing, loading, error, reload }. `pricing` is null until the fees have arrived (or if they could not be fetched):
// callers show a placeholder rather than a made-up number. `fresh` skips the one-minute cache (use it where money is decided).
export const usePricing = ({ fresh = false, startOffer = false } = {}) => {
  const [pricing, setPricing] = useState(cached ? cached.data : null);
  const [loading, setLoading] = useState(fresh || !cached);
  const [error, setError] = useState('');

  const load = useCallback(async (force) => {
    setLoading(true);
    try {
      setPricing(await fetchPricing({ force, startOffer }));
      setError('');
    } catch (err) {
      setError('We could not load the session prices.');
    } finally {
      setLoading(false);
    }
  }, [startOffer]);

  useEffect(() => { load(fresh || startOffer); }, [load, fresh, startOffer]);

  return { pricing, loading, error, reload: () => load(true) };
};

// The fee entry for the session length the customer picked. null while prices are not loaded.
export const priceFor = (pricing, { duration } = {}) => {
  if (!pricing) return null;
  return pricing.durations.find((entry) => entry.value === duration) || null;
};

export const shortestSession = (pricing) => (pricing && pricing.durations.length
  ? [...pricing.durations].sort((a, b) => a.minutes - b.minutes)[0]
  : null);

// "INR 999" for whole rupees, "INR 1178.82" otherwise.
export const formatInr = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'INR —';
  const number = Number(value);
  return `INR ${Number.isInteger(number) ? number : number.toFixed(2)}`;
};
